<script lang="ts" setup>
import type { ToggleGroupItemProps } from 'reka-ui'
import type { SetupContext, Slot, SlotsType } from 'vue'

import LineXClose from '@docue/docue-ui-v2/icons/LineXClose.vue'
import LineFile05 from '@docue/docue-ui-v2/icons/LineFile05.vue'
import LineGlobe02 from '@docue/docue-ui-v2/icons/LineGlobe02.vue'
import LineArrowUp from '@docue/docue-ui-v2/icons/LineArrowUp.vue'
import LineSearchMd from '@docue/docue-ui-v2/icons/LineSearchMd.vue'
import LineArrowDown from '@docue/docue-ui-v2/icons/LineArrowDown.vue'
import LineLayoutAlt02 from '@docue/docue-ui-v2/icons/LineLayoutAlt02.vue'
import {
  ComboboxContent,
  ComboboxInput,
  ComboboxRoot,
  DialogClose,
  FocusScope,
  ToggleGroupItem,
  ToggleGroupRoot,
} from 'reka-ui'

import type { SearchResultType } from '~/types'

import IconSearchAi from '~/components/IconSearchAi.vue'

import type { CommandPaletteTab } from '../AppCommandPalette.vue'

interface SearchHighlightResult { type: SearchResultType, id: string }
interface ChatHighlightResult { type: 'chat', id: string }
type HighlightResult = SearchHighlightResult | ChatHighlightResult
type ActiveFilterType = 'all' | 'document' | 'template'

const emit = defineEmits<{
  closeRequested: []
  tabChange: [tab: CommandPaletteTab]
}>()

const query = defineModel<string>('query', { required: true })

const internalHighlightedResult = ref<HighlightResult>()
const { isTouchDevice } = useTouch()
const { messages: findingAssistantChatMessages } = useFindingAssistantChatHistory()

const { t } = useI18n()
const links = useLinks()
const { activeWorkspaceId, arePremadeTemplatesVisible } = useAuth()

const { enabled: isFindingAssistantChatFeatureEnabled } = useFeatureEnabled('finding-assistant-chat')
const isFindingAssistantChatEnabled = computed(() => arePremadeTemplatesVisible.value && isFindingAssistantChatFeatureEnabled.value)

const activeFilterType = useSessionStorage<ActiveFilterType>(`${unref(activeWorkspaceId) || ''}::search::type`, 'all')
const searchQueryDebounced = refDebounced(query, 300)

const searchParams = computed(() => {
  const params: { query: string, 'filter[type]'?: string } = { query: searchQueryDebounced.value }

  if (activeFilterType.value === 'document')
    params['filter[type]'] = 'document,folder'
  else if (activeFilterType.value === 'template')
    params['filter[type]'] = 'template,user_template'

  return params
})

const {
  data: searchResultsData,
  isLoading,
  isFetched: isSearchResultsFetched,
} = useQuerySearch.global({
  params: searchParams,
  useQueryOptions: {
    enabled: () => !!searchParams.value.query.length,
  },
})

const searchResults = computed(() => searchResultsData.value?.data ?? [])

const { $posthog } = useNuxtApp()
const onResultOpen = async (result: { type: SearchResultType, id: string }, target?: string) => {
  $posthog.capture('command_palette_search_result_opened')

  if (!target) {
    emit('closeRequested')
  }

  switch (result.type) {
    case 'document':
      await navigateTo(links.document(result.id), { ...(target ? { open: { target } } : {}) })
      break
    case 'folder':
      await navigateTo(links.folder(result.id), { ...(target ? { open: { target } } : {}) })
      break
    case 'template':
      await navigateTo(links.template(result.id), { ...(target ? { open: { target } } : {}) })
      break
    case 'user_template':
      await navigateTo(links.userTemplate(result.id), { ...(target ? { open: { target } } : {}) })
      break
  }
}

const VALID_SEARCH_RESULT_TYPES: SearchResultType[] = ['document', 'folder', 'template', 'user_template']

const isSearchResultPayload = (payload: unknown): payload is { _type: SearchResultType, id: string } =>
  typeof payload === 'object'
  && !!payload
  && '_type' in payload
  && typeof payload._type === 'string'
  && VALID_SEARCH_RESULT_TYPES.includes(payload._type as SearchResultType)
  && 'id' in payload
  && typeof payload.id === 'string'

const onHighlight = (payload: unknown) => {
  if (!payload || !isSearchResultPayload(payload))
    return

  internalHighlightedResult.value = { type: payload._type, id: payload.id }
}

const ResultTypeToggle = (props: ToggleGroupItemProps, { slots }: SetupContext<any, SlotsType<{ default: Slot }>>) =>
  h(
    ToggleGroupItem,
    {
      class: 'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-800 ring-1 ring-gray-300 hover:!bg-gray-200/50 data-[state=on]:bg-gray-100 md:gap-2',
      ...props,
    },
    () => slots.default?.(),
  )

const emptyStateMessage = computed(() => {
  if (searchQueryDebounced.value.trim().length && !searchResults.value?.length)
    return t('search.results.no-results')

  if (findingAssistantChatMessages.value.length)
    return t('appCommandPalette.search.emptyState.alternative')

  return t('search.empty-state')
})
</script>

<template>
  <ComboboxRoot
    default-open
    ignore-filter
    :reset-search-term-on-blur="false"
    :reset-search-term-on-select="false"
    class="flex min-h-0 flex-1 flex-col"
    @highlight="(payload) => onHighlight(payload?.value)"
  >
    <ComboboxContent
      class="min-h-0"
      force-mount
      @escape-key-down="emit('closeRequested')"
    >
      <FocusScope
        trapped
        loop
      >
        <div class="mb-3 flex items-center justify-between px-3 pt-3">
          <div class="flex grow items-center justify-start gap-2">
            <component
              :is="isFindingAssistantChatEnabled ? IconSearchAi : LineSearchMd"
              class="size-5 shrink-0 text-gray-600"
            />
            <ComboboxInput
              v-model="query"
              :placeholder="isFindingAssistantChatEnabled ? `${t('appCommandPalette.title')}…` : t('search.placeholder')"
              class="w-full min-w-0 py-2 focus:outline-none"
            />
          </div>
          <DialogClose as-child>
            <DTIconButton variant="plain">
              <LineXClose class="size-5" />
              <span class="sr-only">{{ t('close') }}</span>
            </DTIconButton>
          </DialogClose>
        </div>
        <ToggleGroupRoot
          :model-value="activeFilterType"
          class="flex flex-wrap items-center justify-start gap-2 border-b border-b-gray-200 px-3 pb-3"
          @update:model-value="(value) => value !== 'chat' ? (activeFilterType = value as ActiveFilterType) : void 0"
        >
          <ResultTypeToggle value="all">
            <LineGlobe02 class="size-4" />
            {{ t('search.filter.type-all') }}
          </ResultTypeToggle>
          <ResultTypeToggle value="document">
            <LineFile05 class="size-4" />
            {{ t('search.filter.type-documents') }}
          </ResultTypeToggle>
          <ResultTypeToggle value="template">
            <LineLayoutAlt02 class="size-4" />
            {{ t('search.filter.type-templates') }}
          </ResultTypeToggle>
          <ResultTypeToggle
            v-if="isFindingAssistantChatEnabled"
            value="chat"
            @click.prevent="emit('tabChange', 'chat.new')"
          >
            <IconSearchAi class="size-4" />
            {{ t('appCommandPalette.chat.title') }}
          </ResultTypeToggle>
        </ToggleGroupRoot>
      </FocusScope>
      <div class="flex min-h-0 flex-1 flex-col">
        <div class="relative flex min-h-0 flex-col gap-0.5">
          <div class="h-full max-h-[50dvh] overflow-y-auto py-2">
            <div
              v-if="isLoading || !searchResults?.length"
              class="p-8 text-sm text-gray-600"
            >
              <DTLoaderDots v-if="isLoading" />
              <span v-else>{{ emptyStateMessage }}</span>
            </div>
            <template v-else-if="isSearchResultsFetched">
              <AppCommandPaletteSearchResultItem
                v-for="result in searchResults"
                :key="result.id"
                :item="result"
                @open="onResultOpen"
              />
              <slot
                name="trailing-results"
                :active-filter-type
                :query="searchQueryDebounced"
              />
            </template>
          </div>
        </div>
      </div>
    </ComboboxContent>
  </ComboboxRoot>

  <div
    v-if="(searchResults?.length || findingAssistantChatMessages.length) && !isTouchDevice"
    class="hidden w-full items-center justify-end gap-4 border-t border-t-gray-200 p-3 text-gray-600 sm:flex"
  >
    <div class="flex items-center gap-2">
      <span class="rounded-md border border-black/10 bg-gray-50 px-1 py-0.5 text-xs *:font-sans *:text-xs">
        <LineArrowUp class="size-4" />
        <kbd class="sr-only">↑</kbd>
      </span>
      <span class="rounded-md border border-black/10 bg-gray-50 px-1 py-0.5 text-xs *:font-sans *:text-xs">
        <LineArrowDown class="size-4" />
        <kbd class="sr-only">↓</kbd>
      </span>
      <span class="text-xs">{{ t('appCommandPalette.search.toNavigate') }}</span>
    </div>
    <div class="flex items-center gap-2">
      <span class="rounded-md border border-black/10 bg-gray-50 px-1 py-0.5 text-xs *:font-sans *:text-xs">
        <kbd>⏎</kbd>
      </span>
      <span class="text-xs">{{ t('appCommandPalette.search.toSelect') }}</span>
    </div>
    <div class="flex items-center gap-2">
      <span class="rounded-md border border-black/10 bg-gray-50 px-1 py-0.5 text-xs *:font-sans *:text-xs">
        <kbd>Esc</kbd>
      </span>
      <span class="text-xs">{{ t('appCommandPalette.search.toExit') }}</span>
    </div>
  </div>
</template>
